/**
 * Global Booking Modal - Accessible from anywhere via Zustand store
 */
import { create } from 'zustand';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookingForm } from '@/components/forms/BookingForm';
import { useLanguage } from '@/contexts/LanguageContextNew';

// Zustand store for global modal state
interface BookingModalStore {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const useBookingModal = create<BookingModalStore>((set) => ({
  isOpen: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
}));

const titles = {
  en: 'Book a Call',
  fr: 'Réserver un Appel',
  es: 'Reservar una Llamada',
  ar: 'احجز مكالمة',
  de: 'Anruf Buchen',
  it: 'Prenota una Chiamata',
  pt: 'Agendar Ligação',
  nl: 'Bel Boeken',
  pl: 'Zarezerwuj Rozmowę',
  ru: 'Забронировать Звонок',
  zh: '预约通话',
  ja: '電話を予約',
  hi: 'कॉल बुक करें',
  ro: 'Programează un Apel',
  cs: 'Rezervovat Hovor',
  hu: 'Hívás Foglalás',
  fi: 'Varaa Puhelu',
};

const subtitles = {
  en: 'Schedule a 30-minute discovery call with our team.',
  fr: 'Planifiez un appel découverte de 30 minutes avec notre équipe.',
  es: 'Programa una llamada de descubrimiento de 30 minutos con nuestro equipo.',
  ar: 'حدد موعداً لمكالمة استكشاف مدتها 30 دقيقة مع فريقنا.',
  de: 'Planen Sie ein 30-minütiges Entdeckungsgespräch mit unserem Team.',
  it: 'Programma una chiamata di scoperta di 30 minuti con il nostro team.',
  pt: 'Agende uma chamada de descoberta de 30 minutos com nossa equipe.',
  nl: 'Plan een 30 minuten durend ontdekkingsgesprek met ons team.',
  pl: 'Zaplanuj 30-minutową rozmowę odkrywczą z naszym zespołem.',
  ru: 'Запланируйте 30-минутный ознакомительный звонок с нашей командой.',
  zh: '与我们的团队安排30分钟的探索通话。',
  ja: 'チームと30分のディスカバリーコールを予約してください。',
  hi: 'हमारी टीम के साथ 30 मिनट की खोज कॉल शेड्यूल करें।',
  ro: 'Programați un apel de descoperire de 30 de minute cu echipa noastră.',
  cs: 'Naplánujte 30minutový úvodní hovor s naším týmem.',
  hu: 'Egyeztessen egy 30 perces felfedező hívást csapatunkkal.',
  fi: 'Varaa 30 minuutin tutustumispuhelu tiimimme kanssa.',
};

export function GlobalBookingModal() {
  const { isOpen, close } = useBookingModal();
  const { language, isRTL } = useLanguage();

  const title = titles[language as keyof typeof titles] || titles.en;
  const subtitle = subtitles[language as keyof typeof subtitles] || subtitles.en;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent 
        className="sm:max-w-lg bg-navy border-white/10"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl">{title}</DialogTitle>
          <p className="text-white/60 text-sm">{subtitle}</p>
        </DialogHeader>
        <BookingForm onSuccess={close} />
      </DialogContent>
    </Dialog>
  );
}
